import { useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { subCompaniesApi } from 'app/services/api';
import _ from 'lodash';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { selectLoanSubCompanyId } from 'store/loanApplications/selectors';
import { selectExtendedUsers } from 'store/users/selectors';

import { buildActorDirectoryFromSubCompanyUsers } from '../DisclosureDelivery.utils';

// Directory the disclosure audit strip/trail resolve actor ids against. The global employee
// directory only covers the master company, so a broker (sub-company) actor renders as "Unknown
// user"; layer the loan's sub-company users on top — scoped to this module so broker users don't
// leak into the global employee pickers. Degrades to employees-only for retail loans (no
// subCompanyId) or a failed fetch.
const useDisclosureActorDirectory = ({ enabled = false } = {}) => {
  const employees = useSelector(selectExtendedUsers);
  const subCompanyId = useSelector(selectLoanSubCompanyId);

  const [subCompanyActors, setSubCompanyActors] = useState(() => ({}));
  const latestRequestRef = useRef(0);

  useEffectSafeStrictMode(() => {
    if (!enabled || !subCompanyId) {
      setSubCompanyActors(prev => (_.isEmpty(prev) ? prev : {}));

      return undefined;
    }

    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    subCompaniesApi
      .getSubCompanyUsers({ subCompanyId, includeDescendants: true })
      .then(({ data }) => {
        if (requestId === latestRequestRef.current) {
          setSubCompanyActors(buildActorDirectoryFromSubCompanyUsers(data));
        }
      })
      .catch(_.noop);

    return () => {
      latestRequestRef.current += 1;
    };
  }, [enabled, subCompanyId]);

  return useMemo(() => ({ ...subCompanyActors, ...employees }), [subCompanyActors, employees]);
};

export default useDisclosureActorDirectory;
